/**
 * Open a document that has to be signed before it can be shown.
 *
 * Two rules have to hold at once, and getting either wrong is how a reader
 * ends up staring at a stranded `about:blank`:
 *
 *  1. The tab must be opened SYNCHRONOUSLY inside the click's user gesture.
 *     Signing a Storage path is async, and a `window.open()` that happens
 *     after the await is a popup the browser blocks.
 *  2. The `windowFeatures` argument must NOT carry `noopener` or `noreferrer`.
 *     Per the HTML spec either token makes `window.open()` return `null` —
 *     while still opening the blank tab. The caller then has no handle to
 *     navigate and no handle to close, so the blank tab stays on screen
 *     forever and the fallback `window.open(url, …)` is blocked as a popup
 *     because the gesture is long gone. That is exactly what the owner
 *     portal's handover-protocol links did.
 *
 * The opener reference we get back instead of `noopener` is severed by hand
 * once the tab has been pointed at the signed URL, so the destination still
 * cannot reach back into this app.
 */

/** A blank tab opened inside the gesture, waiting for a URL. */
export type PendingDocumentTab = {
  /** Point the tab at the resolved URL. Falls back to a fresh `open()`. */
  navigate: (url: string) => void;
  /** Give up — close the blank tab so no `about:blank` is left behind. */
  cancel: () => void;
};

/**
 * Only http(s) and blob: URLs may be opened. A stored file value is user
 * data, and the blank tab inherits this app's origin, so a `javascript:` or
 * `data:` value would run as us and could read the session.
 */
export function isOpenableUrl(url: string): boolean {
  return /^(?:https?|blob):/i.test(url);
}

export function openPendingDocumentTab(): PendingDocumentTab {
  // No feature string: any `noopener`/`noreferrer` token would null the handle.
  const tab = window.open("", "_blank");
  return {
    navigate: (url: string) => {
      if (!isOpenableUrl(url)) {
        console.warn("[open-document-tab] refused a non-http(s) URL");
        try {
          tab?.close();
        } catch {
          /* already gone */
        }
        return;
      }
      if (!tab || tab.closed) {
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
      tab.location.href = url;
      // Same protection `noopener` would have given, minus the null handle.
      try {
        tab.opener = null;
      } catch {
        /* cross-origin navigation already severed it */
      }
    },
    cancel: () => {
      try {
        tab?.close();
      } catch {
        /* already gone */
      }
    },
  };
}
